import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Car } from "lucide-react";

export default function Login() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", login);
      await refresh();
      toast.success(`Bienvenue ${data.name}`);
      navigate(data.role === "driver" ? "/dashboard" : "/espace");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", reg);
      await refresh();
      toast.success(`Compte créé, bienvenue ${data.name}`);
      navigate("/espace");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] grain">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-12 sm:py-20 animate-fade-up">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-[#39FF14] mx-auto flex items-center justify-center mb-4 gold-glow">
            <Car className="h-7 w-7 text-[#0B0F17]" />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Espace Dawal</h1>
          <p className="text-slate-400 text-sm mt-2">Connectez-vous à votre compte chauffeur ou client</p>
        </div>

        <div className="card-surface rounded-2xl p-6 sm:p-8">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full bg-[#0B0F17] border border-[#1E293B]">
              <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Créer un compte</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" required value={login.email} data-testid="login-email-input"
                    onChange={(e) => setLogin({ ...login, email: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="vous@email.fr" />
                </div>
                <div>
                  <Label className="text-slate-300">Mot de passe</Label>
                  <Input type="password" required value={login.password} data-testid="login-password-input"
                    onChange={(e) => setLogin({ ...login, password: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Nom complet</Label>
                  <Input required value={reg.name} data-testid="register-name-input"
                    onChange={(e) => setReg({ ...reg, name: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="Jean Dupont" />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" required value={reg.email} data-testid="register-email-input"
                    onChange={(e) => setReg({ ...reg, email: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="vous@email.fr" />
                </div>
                <div>
                  <Label className="text-slate-300">Téléphone</Label>
                  <Input value={reg.phone} data-testid="register-phone-input"
                    onChange={(e) => setReg({ ...reg, phone: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="+33 6 12 34 56 78" />
                </div>
                <div>
                  <Label className="text-slate-300">Mot de passe</Label>
                  <Input type="password" required value={reg.password} data-testid="register-password-input"
                    onChange={(e) => setReg({ ...reg, password: e.target.value })}
                    className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-slate-100" placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit-button"
                  className="w-full bg-[#39FF14] hover:bg-[#5CFF45] text-[#0B0F17] font-semibold">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
